import AOS from 'aos';
import { useEffect, useMemo } from 'react';
import ReactGA from 'react-ga4';
import ReactMarkdown from 'react-markdown';
import { useLocation } from 'react-router-dom';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import remarkGfm from 'remark-gfm';
import { Project } from '../App';
import GithubIcon from '../images/logos/github-small.png';
import { SITE_URL } from '../routes';
import Footer from './Footer';
import NotFound from './NotFound';
import Seo, { ORG_ID, PERSON_ID } from './Seo';

/**
 * Turn a markdown title/description into a clean plain-text meta string.
 *
 * Only markdown *structure* is stripped. Punctuation that carries meaning inside
 * a word is preserved — a blanket /[#*_~`>|-]/ strip turns "A* algorithm" into
 * "A algorithm" and "SHA-256" into "SHA 256", which then ships as the page title.
 *
 * Prefer an explicit seoTitle/seoDescription on the project over relying on this.
 */
function toPlainText(input: string, maxLength: number): string {
	const text = input
		.replace(/```[\s\S]*?```/g, ' ') // fenced code blocks
		.replace(/^\s*\|.*$/gm, ' ') // table rows
		.replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1') // links/images -> their text
		.replace(/^\s{0,3}#{1,6}\s+/gm, '') // ATX headings
		.replace(/^\s{0,3}[>*+-]\s+/gm, '') // blockquote + list markers
		.replace(/(\*\*|__)(.*?)\1/g, '$2') // bold
		.replace(/(?<![\w*])\*(?!\s)([^*]+?)(?<!\s)\*(?![\w*])/g, '$1') // italics
		.replace(/`([^`]+)`/g, '$1') // inline code
		.replace(/\s+/g, ' ')
		.trim();

	if (text.length <= maxLength) return text;
	// Cut on a word boundary rather than mid-word.
	const cut = text.slice(0, maxLength - 1);
	const lastSpace = cut.lastIndexOf(' ');
	return `${(lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

const MEDIA_URL_PREFIX = 'https://ph4sm4-content.s3.eu-central-1.amazonaws.com';

type Props = {
	projects: Project[];
};

export default function ProjectDisplayer({ projects }: Props) {
	const { pathname } = useLocation();

	// Resolved during render, not in an effect: the prerenderer (and any crawler
	// reading the first paint) must see the real project page, not the 404 that
	// an effect-populated state would show on the first render.
	const project = useMemo(
		() =>
			projects.find(
				(p) => p.path.toLocaleLowerCase() === pathname.toLocaleLowerCase()
			) ?? null,
		[projects, pathname]
	);

	const mediaToDisplay = useMemo(() => {
		if (!project) return [];

		return project.media.map((filePath, index) => {
			const src = MEDIA_URL_PREFIX + filePath;
			const label = `${toPlainText(project.title, 60)} — media ${index + 1}`;

			if (filePath.endsWith('.mp4')) {
				return (
					<div className="mt-20" key={src}>
						<video controls width="60%" preload="metadata" aria-label={label}>
							<source src={src} type="video/mp4" />
							Sorry, your browser doesn't support videos.
						</video>
					</div>
				);
			}
			return (
				<img
					key={src}
					src={src}
					alt={label}
					loading="lazy"
					decoding="async"
					className={`mt-20 ${project.fullWidth ? 'w-full' : 'w-3/5'}`}
				/>
			);
		});
	}, [project]);

	useEffect(() => {
		AOS.init();
		window.scrollTo(0, 0);
	}, [pathname]);

	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: pathname,
			title: pathname
		});
	}, [pathname]);

	if (!project) {
		return (
			<main className="mt-60 mb-20 w-full">
				<NotFound />
			</main>
		);
	}

	const name = project.seoTitle ?? toPlainText(project.title, 60);
	const description =
		project.seoDescription ?? toPlainText(project.description, 155);

	return (
		<>
			<Seo
				title={name}
				description={description}
				path={project.path}
				ogType="article"
				jsonLd={[
					{
						'@type': 'SoftwareSourceCode',
						name,
						description,
						url: `${SITE_URL}${project.path}`,
						codeRepository: project.repoUrl || undefined,
						programmingLanguage: project.language,
						author: { '@id': PERSON_ID },
						publisher: { '@id': ORG_ID }
					},
					{
						'@type': 'BreadcrumbList',
						itemListElement: [
							{
								'@type': 'ListItem',
								position: 1,
								name: 'Home',
								item: `${SITE_URL}/`
							},
							{
								'@type': 'ListItem',
								position: 2,
								name,
								item: `${SITE_URL}${project.path}`
							}
						]
					}
				]}
			/>
			<main className="project--displayer mt-40 mb-20 w-full">
				<div
					className="flex w-[80%] mx-auto p-8 flex-col project--content"
					data-aos="fade-down"
					data-aos-duration="1500"
					data-aos-delay="500"
					data-aos-once="true">
					<div className="project--title icons--holder">
						<ReactMarkdown>{project.title}</ReactMarkdown>
						{project.repoUrl && (
							<a
								href={project.repoUrl}
								target="_blank"
								rel="noreferrer"
								aria-label={`${name} source code on GitHub`}>
								<img src={GithubIcon} alt="" width={32} height={32} />
							</a>
						)}
					</div>
					<div className="project--description">
						<ReactMarkdown
							remarkPlugins={[remarkGfm]}
							children={project.description}
							components={{
								a: (props) => {
									return (
										<a
											//@ts-ignore
											href={props.href}
											target="_blank"
											rel="noopener noreferrer">
											{props.children}
										</a>
									);
								},
								table: (props) => {
									return (
										<div className="table-holder">
											<table>{props.children}</table>
										</div>
									);
								},
								code(props) {
									const { children, className, node, ...rest } = props;
									const match = /language-(\w+)/.exec(className || '');
									console.log(className);
									return match ? (
										<SyntaxHighlighter
											{...rest}
											language={match[1]}
											style={atomOneDark}
											customStyle={{
												backgroundColor: ' #202020',
												padding: '20px 30px',
												borderRadius: '20px'
											}}>
											{props.children.toString()}
										</SyntaxHighlighter>
									) : (
										<code {...rest} className={className}>
											{children}
										</code>
									);
								}
							}}></ReactMarkdown>
					</div>
					{mediaToDisplay}
					{project.showcaseComponent}
				</div>
			</main>
			<Footer color="#080808" />
		</>
	);
}
